import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";

import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { updateBidReportAnswersCache } from "lib/hooks/api/bid/answers/useGetBidReportAnswers";
import { updateBidQuery } from "lib/hooks/api/bid/bids/updateBidCache";
import {
  BidOrchestratorSubscriptionStatusEvent,
  useBidOrchestratorStatusSubscription,
} from "lib/hooks/api/bid/bids/useBidOrchestratorStatusSubscription";
import { customerDocumentsKey } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { CurrentUserData, useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { routeSegments } from "lib/routes";
import { useHoldStaleBundleReload } from "lib/staleBundleReload";
import {
  BidOrchestratorPhase,
  BidOrchestratorSection,
  CustomerDocumentTypeFilter,
} from "lib/types/graphQLEnums";
import {
  getisSectionStreamingVals,
  updateSectionStreamingStatus,
} from "./bidOrchestratorStatusUtils";
import { initialiseBidReportQuestions } from "./initialiseBidReportQuestions";
import { BidOrchestratorEntry, QueuedBidOrchestrator } from "./types";
import { closeSectionRelease, updateStreamedReportAnswer } from "./updateStreamedReportAnswer";
import { updateStreamedReportSection } from "./updateStreamedReportSection";

type BidOrchestratorMap = Record<string, BidOrchestratorEntry>;
type QueuedBidOrchestratorMap = Record<string, QueuedBidOrchestrator>;

type BidOrchestratorStreamContextValue = {
  entries: BidOrchestratorMap;
  /**
   * Called by mutation consumers immediately after `startBidOrchestrator` returns.
   * Opens the subscription for this bidId and stamps the entry's runId so stale
   * events from a cancelled prior run (via CANCEL_IN_PROGRESS) are dropped.
   * `generates` records which report sections this run was asked to produce.
   */
  trackOrchestrator: (
    bidId: string,
    runId: string,
    generates: { qualificationReport: boolean; requirementSummary: boolean },
  ) => void;
  /**
   * Per-bid map of orchestrator runs a caller has queued but the driver hasn't
   * dispatched yet. A caller queues one via `queueBidOrchestrator`; the matching
   * BidOrchestratorDriver dispatches `startBidOrchestrator` once uploads settle and
   * `dequeueBidOrchestrator`s it. Lives on the provider so it survives the
   * BidContent → BidReportLoadingPage navigation that happens immediately after
   * submission.
   */
  queuedBidOrchestrators: QueuedBidOrchestratorMap;
  queueBidOrchestrator: (bidId: string, queuedOrchestrator: QueuedBidOrchestrator) => void;
  dequeueBidOrchestrator: (bidId: string) => void;
};

const BidOrchestratorStreamContext = createContext<BidOrchestratorStreamContextValue | undefined>(
  undefined,
);

/**
 * Reduces one subscription event into the next entry state and writes any cache
 * projections it implies. On `*_report_started` we seed the per-section release
 * order and prime the answers cache; `streaming_chunk` events flow through the
 * release gate — the current question types in chunk-by-chunk while later
 * questions are buffered (if complete) or dropped (if mid-stream); on
 * `*_generation_success` we close the release buffer so straggler chunks can't
 * overwrite the persisted shape.
 *
 * Stale-event filtering by runId is the caller's responsibility (see handleEvent).
 *
 * Exported for tests so the orchestration can be exercised without driving the
 * MSW subscription streamBidReport handler.
 */
export function applyEvent(
  prevEntry: BidOrchestratorEntry,
  event: BidOrchestratorSubscriptionStatusEvent,
  queryClient: QueryClient,
  currentUser: CurrentUserData | undefined,
): BidOrchestratorEntry {
  let newEntry: BidOrchestratorEntry = { ...prevEntry };

  if (
    event.status &&
    prevEntry.phase !== BidOrchestratorPhase.Success &&
    prevEntry.phase !== BidOrchestratorPhase.Failed
  ) {
    // Never regress from a terminal phase. Persisted pubsub replay can deliver
    // earlier-phase events after success/failed for a given run.
    newEntry.phase = event.status;
  }

  // Set title and report IDs as they arrive so they're available for cache projection
  if (event.title) {
    const title = event.title;
    newEntry.title = title;
    updateBidQuery(queryClient, newEntry.bidId, (bid) => ({ ...bid, name: title }));
  }
  if (event.qualificationReportId) {
    newEntry.qualificationReportId = event.qualificationReportId;
    updateBidQuery(queryClient, newEntry.bidId, (bid) => ({
      ...bid,
      qualificationReport: {
        id: event.qualificationReportId ?? "",
        createdAt: new Date().toISOString(),
        createdBy: currentUser
          ? {
              guid: currentUser.guid,
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
              active: true,
            }
          : null,
      },
    }));
  }

  if (event.requirementSummaryReportId) {
    newEntry.requirementSummaryReportId = event.requirementSummaryReportId;
    // Update requirement summary report immediately when it's available.
    updateBidQuery(queryClient, newEntry.bidId, (bid) => ({
      ...bid,
      bidReport: {
        id: event.requirementSummaryReportId ?? "",
        createdAt: new Date().toISOString(),
        createdBy: currentUser
          ? {
              guid: currentUser.guid,
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
              active: true,
            }
          : null,
      },
    }));
  }

  const startedPhase =
    event.status === BidOrchestratorPhase.QualificationReportStarted ||
    event.status === BidOrchestratorPhase.RequirementSummaryReportStarted;
  if (startedPhase && event.streamingQuestions) {
    newEntry = initialiseBidReportQuestions({
      newEntry,
      streamingQuestions: event.streamingQuestions,
      phase: event.status,
      queryClient,
    });
  }

  // When files are processed successfully, invalidate the customerDocuments query so
  // the UI can show the new documents that were just processed.
  if (
    event.status === BidOrchestratorPhase.FilesSuccessfullyProcessed &&
    prevEntry.phase !== BidOrchestratorPhase.FilesSuccessfullyProcessed
  ) {
    void queryClient.invalidateQueries({
      queryKey: customerDocumentsKey({
        documentType: CustomerDocumentTypeFilter.BidDocument,
        resourceId: newEntry.bidId,
        uiPath: null,
      }),
    });
  }

  // Update the status of the section in response to published lifecycle events for that section,
  // for example we use this to determine if there's been an error or if a section is still streaming
  newEntry = updateSectionStreamingStatus(event.status, newEntry, prevEntry.phase);

  // On per-section terminal, close the release buffer so any late chunk arriving
  // after `*_generation_success` can't overwrite the persisted shape, and project
  // the report row onto the bid query so the UI sees it without waiting for a refetch.

  if (event.status === BidOrchestratorPhase.QualificationReportGenerationSuccess) {
    newEntry = closeSectionRelease(newEntry, BidOrchestratorSection.QualificationReport);
    // Hydrate the cache with the persisted shape and toast once. Dedup against
    // a replay by checking the previous section status: any subsequent replay
    // of this same event will see "complete" and short-circuit.
    if (
      prevEntry.sectionStatus[BidOrchestratorSection.QualificationReport] !== "complete" &&
      newEntry.qualificationReportId &&
      event.qualificationReport
    ) {
      const report = event.qualificationReport;
      updateBidReportAnswersCache(queryClient, newEntry.qualificationReportId, (prev) => ({
        ...prev,
        result: report.result,
      }));
    }
  }
  if (event.status === BidOrchestratorPhase.RequirementSummaryReportGenerationSuccess) {
    newEntry = closeSectionRelease(newEntry, BidOrchestratorSection.RequirementsSummary);
    if (
      prevEntry.sectionStatus[BidOrchestratorSection.RequirementsSummary] !== "complete" &&
      newEntry.requirementSummaryReportId &&
      event.requirementSummaryReport
    ) {
      const report = event.requirementSummaryReport;
      updateBidReportAnswersCache(queryClient, newEntry.requirementSummaryReportId, (prev) => ({
        ...prev,
        result: report.result,
      }));
    }
  }

  if (event.status === BidOrchestratorPhase.StreamingChunk && event.section && !!event.content) {
    // Single switch on event.section — each branch owns both the entry-state
    // update and the targeted cache write, so routing lives in one place.
    if (
      event.section === BidOrchestratorSection.QualificationSummary ||
      event.section === BidOrchestratorSection.WinThemes
    ) {
      newEntry = updateStreamedReportSection({
        nextEntry: newEntry,
        sectionType: event.section,
        eventContent: event.content,
        isComplete: event.isComplete === true,
        sources: event.sources,
        queryClient,
      });
    } else {
      newEntry = updateStreamedReportAnswer({
        nextEntry: newEntry,
        sectionType: event.section,
        streamedAnswer: event.content,
        bidQuestionId: event.bidQuestionId,
        isComplete: event.isComplete,
        sources: event.sources,
        queryClient,
      });
    }
  }

  return newEntry;
}

/**
 * Initial shape of an orchestrator entry — phase = ProcessingFiles, all sections
 * idle, empty release buffers, null streaming sections. Report IDs are
 * intentionally left unset; they get stamped on by `applyEvent` when the
 * `*_report_started` event arrives. Used by `trackOrchestrator` in production
 * and by test fixtures (`test_utils.BASE_ENTRY`) so both code paths share one
 * canonical starting state.
 */
export function createInitialOrchestratorEntry({
  bidId,
  runId,
  generates,
}: {
  bidId: string;
  runId: string;
  generates: { qualificationReport: boolean; requirementSummary: boolean };
}): BidOrchestratorEntry {
  return {
    bidId,
    runId,
    phase: BidOrchestratorPhase.ProcessingFiles,
    generates,
    sectionStatus: {
      [BidOrchestratorSection.QualificationReport]: "idle",
      [BidOrchestratorSection.RequirementsSummary]: "idle",
      [BidOrchestratorSection.QualificationSummary]: "idle",
      [BidOrchestratorSection.WinThemes]: "idle",
    },
    bidReportRelease: {
      [BidOrchestratorSection.QualificationReport]: {
        sortedQuestions: [],
        nextReleaseIndex: 0,
        pendingAnswers: {},
      },
      [BidOrchestratorSection.RequirementsSummary]: {
        sortedQuestions: [],
        nextReleaseIndex: 0,
        pendingAnswers: {},
      },
    },
    streamingReportSections: {
      [BidOrchestratorSection.QualificationSummary]: null,
      [BidOrchestratorSection.WinThemes]: null,
    },
  };
}

/**
 * Display a toast success when a report is generated, with a CTA to view the report. We check the current
 * path to avoid showing the toast if the user is already on the report page, as they don't need a CTA to get there.
 */
export function toastReportGenerationSuccess(entry: BidOrchestratorEntry) {
  if (entry.generates.qualificationReport && entry.qualificationReportId) {
    // Suppress the toast when the user is already on the qualification
    // report — they don't need a "View" CTA back to the page they're on.
    if (
      !window.location.pathname.includes(
        `/bids/${entry.bidId}/${routeSegments.qualificationReport}/${entry.qualificationReportId}`,
      )
    ) {
      toast.success("Qualification report generated", {
        action: (
          <Button
            as="a"
            href={`/bids/${entry.bidId}/${routeSegments.qualificationReport}/${entry.qualificationReportId}`}
          >
            View
          </Button>
        ),
      });
    }
  }

  if (entry.generates.requirementSummary && entry.requirementSummaryReportId) {
    // Suppress the toast when the user is already on the requirement summary
    // (see qualification branch above for rationale).
    if (
      !window.location.pathname.includes(
        `/bids/${entry.bidId}/${routeSegments.requirementSummary}/${entry.requirementSummaryReportId}`,
      )
    ) {
      toast.success("Requirement summary generated", {
        action: (
          <Button
            as="a"
            href={`/bids/${entry.bidId}/${routeSegments.requirementSummary}/${entry.requirementSummaryReportId}`}
          >
            View
          </Button>
        ),
      });
    }
  }
}

export function BidOrchestratorStreamProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const [entries, setEntries] = useState<BidOrchestratorMap>({});
  const [queuedBidOrchestrators, setQueuedBidOrchestrators] = useState<QueuedBidOrchestratorMap>(
    {},
  );
  // Track which bidIds are actively subscribed so the render tree spawns a
  // subscription component per entry. Separate from the entry state so the
  // subscription component doesn't re-render when event content changes.
  const [activeBidIds, setActiveBidIds] = useState<string[]>([]);

  const queueBidOrchestrator = useCallback(
    (bidId: string, queuedOrchestrator: QueuedBidOrchestrator) => {
      setQueuedBidOrchestrators((prev) => ({ ...prev, [bidId]: queuedOrchestrator }));
    },
    [],
  );

  const dequeueBidOrchestrator = useCallback((bidId: string) => {
    setQueuedBidOrchestrators((prev) => {
      if (!(bidId in prev)) return prev;
      const next = { ...prev };
      delete next[bidId];
      return next;
    });
  }, []);

  // We hold the latest entries in a ref so the subscription onData callback
  // can merge against the freshest state without needing to be in the
  // reducer's dependency list.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const trackOrchestrator = useCallback(
    (
      bidId: string,
      runId: string,
      generates: { qualificationReport: boolean; requirementSummary: boolean },
    ) => {
      setEntries((prev) => ({
        ...prev,
        [bidId]: createInitialOrchestratorEntry({ bidId, runId, generates }),
      }));
      setActiveBidIds((prev) => (prev.includes(bidId) ? prev : [...prev, bidId]));
    },
    [],
  );

  const onTerminalEvent = useCallback(
    (
      bidId: string,
      entry: BidOrchestratorEntry,
      phase: BidOrchestratorPhase,
      errorMessage?: string | null,
    ) => {
      // Invalidate the bid query so persisted win themes (with real DB ids) replace
      // the synthetic streaming state. We refetch because the run-wide `success` event
      // carries no win-themes payload, so we have no hydrated shape to project here.
      // Fix if BID-1397 is still an issue: A `win_themes_complete` event from the backend
      // carrying the hydrated themes would let us drop this refetch in favour of setQueryData.
      if (phase === BidOrchestratorPhase.Success) {
        void queryClient.invalidateQueries({ queryKey: ["bid", entry.bidId] });

        // Publish the toast success on a terminal event
        toastReportGenerationSuccess(entry);
      }

      if (phase === BidOrchestratorPhase.Failed) {
        toast.error(errorMessage ?? "bid orchestrator failed");
      }

      setActiveBidIds((prev) => prev.filter((id) => id !== bidId));
    },
    [queryClient],
  );

  const handleEvent = useCallback(
    (bidId: string, event: BidOrchestratorSubscriptionStatusEvent) => {
      const current = entriesRef.current[bidId];
      if (!current) return;

      // Stale-event filter: a replayed `success` from a cancelled prior run could
      // fire onTerminalEvent and unmount the active subscription mid-stream, and
      // stale `*_generation_success` events could overwrite live cache writes.
      // The server also clears persisted pubsub events on every streamBidReport
      // dispatch; this is defense in depth.
      if (event.runId && event.runId !== current.runId) return;

      const nextEntry = applyEvent(current, event, queryClient, currentUser);
      // Sync the ref synchronously so a second event firing before React
      // re-renders still sees this entry's accumulated `pendingAnswers`.
      // Without this, two rapid `isComplete` chunks both read the same stale
      // snapshot and the second `setEntries` wins, dropping the first
      // callback's buffered completion.
      entriesRef.current = { ...entriesRef.current, [bidId]: nextEntry };
      setEntries((prev) => ({ ...prev, [bidId]: nextEntry }));

      const isTerminal =
        event.status === BidOrchestratorPhase.Success ||
        event.status === BidOrchestratorPhase.Failed;
      if (isTerminal && current.phase !== event.status) {
        onTerminalEvent(bidId, nextEntry, event.status, event.errorMessage);
      }
    },
    [queryClient, onTerminalEvent, currentUser],
  );

  // Queued runs live only in memory until they start, and a reload drops the live report stream.
  useHoldStaleBundleReload(
    activeBidIds.length > 0 || Object.keys(queuedBidOrchestrators).length > 0,
  );

  const value = useMemo<BidOrchestratorStreamContextValue>(
    () => ({
      entries,
      trackOrchestrator,
      queuedBidOrchestrators,
      queueBidOrchestrator,
      dequeueBidOrchestrator,
    }),
    [
      entries,
      trackOrchestrator,
      queuedBidOrchestrators,
      queueBidOrchestrator,
      dequeueBidOrchestrator,
    ],
  );

  return (
    <BidOrchestratorStreamContext.Provider value={value}>
      {activeBidIds.map((bidId) => (
        <BidOrchestratorSubscriber
          key={`${bidId}:${entries[bidId]?.runId ?? ""}`}
          bidId={bidId}
          onEvent={handleEvent}
        />
      ))}
      {children}
    </BidOrchestratorStreamContext.Provider>
  );
}

/**
 * Renders one subscription per actively tracked bidId. useSubscription can't be
 * called in a loop, so this sibling component exists purely so the provider can
 * open N subscriptions for N concurrently streaming bids. Keying by bidId:runId
 * forces a remount when a re-trigger replaces the runId, which resets the
 * subscription cleanly.
 */
function BidOrchestratorSubscriber({
  bidId,
  onEvent,
}: {
  bidId: string;
  onEvent: (bidId: string, event: BidOrchestratorSubscriptionStatusEvent) => void;
}) {
  useBidOrchestratorStatusSubscription(bidId, {
    onData: (data) => {
      if (!data?.bidOrchestratorStatus) return;
      onEvent(bidId, data.bidOrchestratorStatus);
    },
  });
  return null;
}

export function useBidOrchestratorStreamContext(): BidOrchestratorStreamContextValue {
  const context = useContext(BidOrchestratorStreamContext);
  if (!context) {
    throw new Error(
      "useBidOrchestratorStreamContext must be used within a BidOrchestratorStreamProvider",
    );
  }
  return context;
}

export type UseBidOrchestratorStreamResult = {
  entry: BidOrchestratorEntry | undefined;
  /** Aggregate flag — true while ANY section is still active. */
  isStreaming: boolean;
  isQualificationReportStreaming: boolean;
  isRequirementSummaryStreaming: boolean;
  isQualificationSummaryStreaming: boolean;
  isWinThemeStreaming: boolean;
  hasFailed: boolean;
  phase: BidOrchestratorPhase;
};
/**
 * Read-only view of a single bid's orchestrator state. Returns `undefined` when
 * the context has no entry for this bidId (i.e. orchestrator hasn't been started
 * in this session). Consumers should treat `undefined` as "fall back to the
 * persisted view from useBidReport".
 *
 * The `is*Streaming` flags are derived from per-section lifecycle (started /
 * generation_success / chunk-isComplete / run-wide terminal). Use the
 * targeted flag in each report section so the UI doesn't show the loading spinner
 * on a section that's already finished while another is still streaming.
 */
export function useBidOrchestratorStream(
  bidId: string | undefined,
): UseBidOrchestratorStreamResult {
  const { entries } = useBidOrchestratorStreamContext();
  const entry = bidId ? entries[bidId] : undefined;

  return useMemo(() => {
    if (!entry) {
      return {
        entry: undefined,
        isStreaming: false,
        isQualificationReportStreaming: false,
        isRequirementSummaryStreaming: false,
        isQualificationSummaryStreaming: false,
        isWinThemeStreaming: false,
        hasFailed: false,
        phase: BidOrchestratorPhase.Idle,
      };
    }

    const {
      isStreaming,
      isQualificationReportStreaming,
      isRequirementSummaryStreaming,
      isQualificationSummaryStreaming,
      isWinThemeStreaming,
    } = getisSectionStreamingVals(entry);

    return {
      entry,
      isStreaming,
      isQualificationReportStreaming,
      isRequirementSummaryStreaming,
      isQualificationSummaryStreaming,
      isWinThemeStreaming,
      hasFailed: entry.phase === BidOrchestratorPhase.Failed,
      phase: entry.phase,
    };
  }, [entry]);
}
